import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react/polaris';
import { LocationType, ShiftInput } from '@amzn/red-velvet-api';
import { useTableColumns } from '../../../../hooks/tableColumns';
import { useTimezone } from '../../../../hooks/timezone';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { asDateString, asTimeString } from '../../../../utils/dateFormatting';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { usePreferencesStrings } from '../../../../hooks/localization/preferencesStrings';
import { useLocale } from '../../../../hooks/locale';

const DEFAULT_SHIFT_TABLE_PAGE_SIZE = 10;

type ShiftTableProps = {
  shifts: ShiftInput[];
  eventLocationType?: LocationType;
};

export function ShiftPreviewTable(props: ShiftTableProps) {
  const selectedTimezone = useTimezone();
  const [shiftPageSize, setShiftPageSize] = useState(DEFAULT_SHIFT_TABLE_PAGE_SIZE);
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const locale = useLocale();
  const propertyFilterStrings = usePreferencesStrings('preferences', [5, 10, 25, 50], namespaces);

  const getPreviewShiftColumnMap = () => {
    return {
      shiftName: (item: ShiftInput) => (item.name ? item.name : '-'),
      shiftUserStatus: () => {
        return (
          <StatusIndicator type={'stopped'} colorOverride={'grey'}>
            {t('shiftsPreviewTable.notSignedUp')}
          </StatusIndicator>
        );
      },
      shiftAttendanceTrackStatus: () => {
        return <Badge color={'green'}>{t('shiftsPreviewTable.upcoming')}</Badge>;
      },
      shiftLocation: (item: ShiftInput) => {
        const locType =
          item.useEventLocation !== false ? props.eventLocationType : item.location?.locationType;
        if (locType === LocationType.VIRTUAL) {
          return t('eventDetailsStep.virtual');
        } else if (locType === LocationType.ON_SITE) {
          return t('eventDetailsStep.onCampus');
        }
        return t('eventDetailsStep.offCampus');
      },
      shiftStartDate: (item: ShiftInput) =>
        asDateString(item.startDateTime, selectedTimezone, locale),
      shiftStartTime: (item: ShiftInput) =>
        asTimeString(item.startDateTime, selectedTimezone, locale, {
          removeTimeZone: true,
        }),
      shiftSignupNumber: (item: ShiftInput) => {
        return t('shiftsPreviewTable.numberOfSignupsOnly', {
          count: 0,
          capacity: item.maxSignup ?? 0,
        });
      },
      shiftAttendeesNumber: () => '0',
    };
  };

  const { columnDefinitions } = useTableColumns(
    getPreviewShiftColumnMap(),
    'shiftsPreviewTable',
    namespaces
  );

  const renderEmptyState = (action: () => void) => {
    return (
      <Box color="inherit">
        <SpaceBetween direction="vertical" size="xs">
          <Box variant="strong" color="inherit">
            {t('shiftsPreviewTable.noMatch')}
          </Box>
          <Button onClick={action}>{t('shiftsPreviewTable.clearFilter')}</Button>
        </SpaceBetween>
      </Box>
    );
  };

  const { items, actions, filteredItemsCount, filterProps, collectionProps, paginationProps } =
    useCollection(props.shifts, {
      filtering: {
        empty: renderEmptyState(() => {
          actions.setFiltering('');
        }),
        noMatch: renderEmptyState(() => {
          actions.setFiltering('');
        }),
      },
      pagination: { pageSize: shiftPageSize },
    });

  return (
    <>
      {props.shifts.length !== 0 && (
        <Table
          {...collectionProps}
          header={
            <Header counter={`(${props.shifts.length})`}>
              {t('shiftsPreviewTable.shiftTableTitle')}
            </Header>
          }
          columnDefinitions={columnDefinitions}
          stickyColumns={{ last: 1 }}
          pagination={<Pagination {...paginationProps} />}
          preferences={
            <CollectionPreferences
              {...propertyFilterStrings}
              onConfirm={({ detail }) => setShiftPageSize(detail.pageSize || 10)}
              preferences={{ pageSize: shiftPageSize }}
            />
          }
          items={items}
          data-testid={'shifts-preview-table'}
          filter={
            <TextFilter
              {...filterProps}
              countText={t('shiftsPreviewTable.matchesCount', { count: filteredItemsCount })}
            />
          }
        />
      )}
    </>
  );
}
